import { useEffect, useState } from "react";
import { Grid, FormControl, Select, MenuItem, Typography } from "@mui/material";
import PackageCard from "./PackageCard";
import { getPackagesList, getProgramList } from "../../api/pricingplan";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import CustomPagination from "../../components/pagination/CustomPagination";
import PricingPlanInfoModal from "./PricingPlanInfoModal";

function PricingPlan() {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const initialAcademicLevel = localStorage.getItem("program");
  const [packages, setPackages] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedAcademicLevel, setSelectedAcademicLevel] = useState(() => {
    if (initialAcademicLevel === "Undergraduate") return 13;
    if (initialAcademicLevel === "Postgraduate") return 14;
    return "";
  });
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("No program selected yet");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    nextPage: null,
    prevPage: null,
  });
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(3);
  const options = [3, 6, 9];

  const handleSelect = (value) => {
    setCount(value);
    setOpen(false);
  };

  // Fetch programs when academic level changes
  useEffect(() => {
    const fetchPrograms = async () => {
      if (!selectedAcademicLevel) return;
      try {
        const res = await getProgramList({
          sub_service_id: selectedAcademicLevel,
          user_id: userId,
        });
        setPrograms(res?.data || []);
      } catch (err) {
        console.error("Could not fetch programs", err);
      }
    };

    fetchPrograms();
  }, [selectedAcademicLevel]);

  const fetchPackages = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getPackagesList({
        requestData: {
          sub_service_id: selectedAcademicLevel,
          program_id: selectedProgram,
          scholar_class: selectedClass,
          user_id: userId,
          page,
        },
        perPage: count,
      });

      const { data = [], pagination: meta } = res || {};

      setPackages(data);

      setPagination({
        currentPage: meta?.currentPage || 1,
        totalPages: meta?.totalPages || 1,
        nextPage: meta?.nextPage,
        prevPage: meta?.previousPage,
      });

      setMessage("No packages found");
    } catch (err) {
      console.error("Could not fetch packages", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedAcademicLevel || !selectedProgram) return;
    fetchPackages(1);
  }, [selectedAcademicLevel, selectedProgram, selectedClass, count]);

  return (
    <div>
      {/* Heading */}
      <h1
        style={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontStyle: "normal",
          fontSize: "30px",
          lineHeight: "100%",
          marginBottom: "1.5rem",
        }}
      >
        Get Started with the{" "}
        <span style={{ color: "#572575" }}>Right Plan</span>
      </h1>

      {/* Form Grid */}
      <Grid container spacing={3}>
        {/* Academic Level Dropdown */}
        <Grid item xs={12} md={6} lg={4}>
          <label
            htmlFor="academic-level"
            style={{
              display: "block",
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            Select Academic Level
            <span style={{ color: "red" }}> *</span>
          </label>
          <FormControl fullWidth>
            <Select
              id="academic-level"
              value={selectedAcademicLevel}
              onChange={(e) => {
                setSelectedAcademicLevel(e.target.value);
                setPrograms([]);
                setSelectedProgram("");
                setSelectedClass("");
              }}
              displayEmpty
              sx={{ borderRadius: "10px", height: "40px" }}
            >
              <MenuItem value="" disabled>
                Select Academic Level
              </MenuItem>
              <MenuItem value={13}>Undergraduate</MenuItem>
              <MenuItem value={14}>Postgraduate</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Program Dropdown */}
        <Grid item xs={12} md={6} lg={4}>
          <label
            htmlFor="select-program"
            style={{
              display: "block",
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "16px",
              marginBottom: "8px",
            }}
          >
            Select Program
            <span style={{ color: "red" }}> *</span>
          </label>
          <FormControl fullWidth>
            <Select
              id="select-program"
              value={selectedProgram}
              onChange={(e) => {
                setSelectedProgram(e.target.value);
                setSelectedClass("");
              }}
              displayEmpty
              sx={{ borderRadius: "10px", height: "40px" }}
            >
              <MenuItem value="" disabled>
                Choose Program
              </MenuItem>
              {programs.map((program) => (
                <MenuItem key={program.id} value={program.id}>
                  {program.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {selectedAcademicLevel === 13 && selectedProgram && (
          <Grid item xs={12} md={6} lg={4}>
            <label
              htmlFor="class-select"
              style={{
                display: "block",
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                marginBottom: "8px",
              }}
            >
              Select Class
            </label>
            <FormControl fullWidth>
              <Select
                id="class-select"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                displayEmpty
                sx={{ borderRadius: "10px", height: "40px" }}
              >
                <MenuItem value="" disabled>
                  Choose Class
                </MenuItem>
                <MenuItem value="class_9">Class 9</MenuItem>
                <MenuItem value="class_10">Class 10</MenuItem>
                <MenuItem value="class_11">Class 11</MenuItem>
                <MenuItem value="class_12">Class 12</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {/* Package Cards Grid */}
      {packages.length == 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "3rem 1rem",   
            backgroundImage: "url('/SAPImages/pricing/image 2816.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            width: "100%",
            minHeight: "400px",
          }}
        >
          <img
            src="/SAPImages/pricing/Group (5).png"
            alt="No packages"
            style={{ width: "178px", height: "231px", marginBottom: "1.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "24px",
              textAlign: "center",
              marginBottom: "0.5rem",
            }}
          >
            {message}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: "16px",
              textAlign: "center",
              color: "#555",
            }}
          >
            Please choose academic level and program to get started.
          </Typography>
          {/* Enroll Button */}
          <div className="my-4 flex justify-center items-center">
            <button
              onClick={() => {
                setOpenInfoModal(true);
              }}
              className="flex items-center justify-center gap-2 bg-[#572575] text-white font-semibold text-base py-2 px-28 rounded-lg shadow-md hover:bg-[#4b1e65] transition-all"
            >
              <img
                src="/SAPImages/pricing/ic_round-co-present.png"
                alt="Enroll"
                className="w-5 h-5"
              />
              More Info
            </button>
            <PricingPlanInfoModal
              open={openInfoModal}
              onClose={() => setOpenInfoModal(false)}
            />
          </div>
        </div>
      ) : loading ? (
        <Loader text="Loading Packages...." />
      ) : (
        <Grid container spacing={3} sx={{ marginTop: "0.5rem" }}>
          {packages.map((pkg, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <PackageCard {...pkg} />
            </Grid>
          ))}
          {/* Pagination */}
          <Grid item xs={12}>
            <CustomPagination
              pagination={pagination}
              onPageChange={(page) => fetchPackages(page)}
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
}

export default PricingPlan;
